/**
 * Durations, estimates and time taken are stored as decimal hours
 * (1.5 = 1h 30m, 1.03 = 1h 2m). These helpers convert to and from a
 * human-readable "Xh Ym" form so nobody has to do the arithmetic.
 */

/**
 * Length of a newly added time log: it starts now and ends this many minutes
 * later. Users then move the end time, and duration follows the range.
 */
export const DEFAULT_TIME_LOG_MINUTES = 20;

/** Compact dayjs format for log times; the year is left out to save space. */
export const COMPACT_DATE_TIME_FORMAT = 'MMM D, HH:mm';

/**
 * Formats decimal hours into "Xh Ym".
 * @param {number|string|null|undefined} hours - Decimal hours (e.g. 1.5).
 * @returns {string} e.g. "1h 30m".
 */
export const formatFloatToTimeHM = (hours) => {
  const numValue = parseFloat(hours);
  if (isNaN(numValue)) {
    return '0h 0m';
  }
  let totalMinutes = Math.round(Math.abs(numValue) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${numValue < 0 ? '-' : ''}${h}h ${m}m`;
};

/**
 * Parses "Xh Ym", "H:MM" or a plain decimal-hours string into decimal hours.
 * e.g. "1h 30m" -> 1.5, "1:02" -> 1.03, "1.5" -> 1.5
 * @param {string} timeString - The time string to parse.
 * @returns {number} Decimal hours, or NaN if invalid.
 */
export const parseTimeHMToFloat = (timeString) => {
  if (timeString === null || timeString === undefined) return NaN;
  const trimmedString = String(timeString).trim().toLowerCase();
  if (trimmedString === '') return NaN;

  // "Xh Ym" (either part optional)
  const hmMatch = trimmedString.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?$/i);
  if (hmMatch && (hmMatch[1] !== undefined || hmMatch[2] !== undefined)) {
    const hours = parseInt(hmMatch[1] || '0', 10);
    const minutes = parseInt(hmMatch[2] || '0', 10);
    if (minutes >= 60) return NaN;
    return parseFloat((hours + minutes / 60).toFixed(2));
  }

  // "H:MM"
  const colonMatch = trimmedString.match(/^(\d+):([0-5]\d)$/);
  if (colonMatch) {
    return parseFloat((parseInt(colonMatch[1], 10) + parseInt(colonMatch[2], 10) / 60).toFixed(2));
  }

  // Plain decimal hours
  if (/^\d+(\.\d+)?$/.test(trimmedString)) {
    return parseFloat(Number(trimmedString).toFixed(2));
  }

  return NaN;
};
